import { Data as ArticleData, DataComments } from '@root/modules/article/types/article';
import { Config } from '@root/modules/channel/types/channel';
import DefaultDataAdapter from '@root/common/utils/DefaultDataAdapter';
import defaultsConfig from '@root/config/defaults.config';

import { convertArrayToObject } from '@root/common/utils/helpers';
import getArticleGroupedBody from './getArticleGroupedBody';
import { HeadlineWithRelatedArticles } from '@headlines/types/headline';

export class ArticleAdapter extends DefaultDataAdapter {
  private _article: ArticleData;
  private _settings: Config['page']['article']['component'];

  constructor(originalData: ArticleData | HeadlineWithRelatedArticles, settings: Config['page']['article']['component']) {
    super();
    this._article = originalData as ArticleData;
    this._settings = settings;
  }

  private article() {
    const article = this._article;

    article.settings = convertArrayToObject<ArticleData['settings']>(article.settings, defaultsConfig.article.settings);
    article.comments = convertArrayToObject<ArticleData['comments']>(article.comments, defaultsConfig.article.comments);
    article.banners = convertArrayToObject<ArticleData['banners']>(article.banners, defaultsConfig.article.banners);
    article.voiceSettings = convertArrayToObject<ArticleData['voiceSettings']>(article.voiceSettings, defaultsConfig.article.voiceSettings);
    article.externalTracker = convertArrayToObject<ArticleData['externalTracker']>(article.externalTracker);
    article.seoFields = convertArrayToObject<ArticleData['seoFields']>(article.seoFields, defaultsConfig.article.seoFields);

    if (!article.content.body) {
      article.content.body = {} as ArticleData['content']['body'];
      article.content.body.content = article.content.body.content || [];
    }

    const groupedBody = getArticleGroupedBody(article, this._settings);
    article.content = this.content({ ...article.content, groupedBody }).all();
    article.primaryCategory = this.primaryCategory(article.primaryCategory).all();
    article.authors = article.authors || defaultsConfig.article.authors;
    article.sources = article.sources || defaultsConfig.article.sources;

    if (!article.relatedEntities?.articles) {
      article.relatedEntities = article.relatedEntities || {};
      article.relatedEntities.articles = { items: [] };
    }

    if (!article.tags) {
      article.tags = { items: [] };
    }

    return article;
  }

  public adapt() {
    const article = this.article();
    return article;
  }
}

export class ArticleCommentsAdapter extends DefaultDataAdapter {
  private _article: DataComments;

  constructor(originalData: DataComments) {
    super();
    this._article = originalData;
  }

  private article() {
    const article = this._article;

    article.comments = convertArrayToObject<ArticleData['comments']>(article.comments, defaultsConfig.article.comments);
    article.content = this.content(article.content).pick(['title', 'lead', 'paywall']);

    article.primaryCategory = this.primaryCategory(article.primaryCategory).all();

    return article;
  }

  public adapt() {
    const article = this.article();
    return article;
  }
}
